// src/services/AdminService.ts - FIXED to match updated API

import { adminApi } from '@/lib/api/adminApi';
import {
    SystemStats,
    OrganisationWithCounts,
    CompleteOrganisationDetails,
    CreateCompleteEmployerRequest,
    CreateOrganisationRequest,
    CreateUnitRequest,
    CreateEmployerRequest,
} from '@/lib/models/admin';
import {
    Organisation,
    Unit,
    UpdateOrganisationRequest,
    UpdateUnitRequest,
    UpdateEmployerRequest,
    EmployerWithDetails,
} from '@/lib/models/employer';

export class AdminService {

    // ==================== DASHBOARD OPERATIONS ====================

    /**
     * Get system statistics (overview for admin dashboard)
     */
    static async getSystemStats(): Promise<SystemStats> {
        console.log('📊 [AdminService] Getting system statistics');
        return await adminApi.getSystemStats();
    }

    /**
     * List all organisations with counts
     * ✅ FIXED: No pagination params
     */
    static async listOrganisationsWithCounts(): Promise<OrganisationWithCounts[]> {
        console.log('📋 [AdminService] Listing organisations with counts');
        return await adminApi.listOrganisationsWithCounts();
    }

    /**
     * Get complete organisation details (org + units + employers)
     */
    static async getCompleteOrganisation(
        organisationId: string
    ): Promise<CompleteOrganisationDetails> {
        console.log('🔍 [AdminService] Getting complete organisation:', organisationId);
        return await adminApi.getCompleteOrganisation(organisationId);
    }

    // ==================== ORGANISATION MANAGEMENT ====================

    /**
     * Create a new organisation
     */
    static async createOrganisation(data: CreateOrganisationRequest): Promise<Organisation> {
        console.log('➕ [AdminService] Creating organisation:', data.name);
        return await adminApi.createOrganisation(data);
    }

    /**
     * Update an organisation
     */
    static async updateOrganisation(
        organisationId: string,
        data: UpdateOrganisationRequest
    ): Promise<Organisation> {
        console.log('💾 [AdminService] Updating organisation:', organisationId);
        return await adminApi.updateOrganisation(organisationId, data);
    }

    /**
     * Delete an organisation
     */
    static async deleteOrganisation(organisationId: string): Promise<void> {
        console.log('🗑️ [AdminService] Deleting organisation:', organisationId);
        return await adminApi.deleteOrganisation(organisationId);
    }

    // ==================== UNIT MANAGEMENT ====================

    /**
     * Create a new unit
     */
    static async createUnit(data: CreateUnitRequest): Promise<Unit> {
        console.log('➕ [AdminService] Creating unit:', data.name);
        return await adminApi.createUnit(data);
    }

    /**
     * Update a unit
     */
    static async updateUnit(unitId: string, data: UpdateUnitRequest): Promise<Unit> {
        console.log('💾 [AdminService] Updating unit:', unitId);
        return await adminApi.updateUnit(unitId, data);
    }

    /**
     * Delete a unit
     */
    static async deleteUnit(unitId: string): Promise<void> {
        console.log('🗑️ [AdminService] Deleting unit:', unitId);
        return await adminApi.deleteUnit(unitId);
    }

    // ==================== EMPLOYER MANAGEMENT ====================

    /**
     * Create complete employer setup (org + unit + user + employer)
     * ⭐ This is the KEY method for onboarding new customers!
     */
    static async createCompleteEmployer(
        data: CreateCompleteEmployerRequest
    ): Promise<EmployerWithDetails> {
        console.log('🏢 [AdminService] Creating complete employer setup for:', data.employer.email);
        return await adminApi.createCompleteEmployer(data);
    }

    /**
     * Create employer for existing unit
     */
    static async createEmployer(data: CreateEmployerRequest): Promise<EmployerWithDetails> {
        console.log('➕ [AdminService] Creating employer for existing unit');
        return await adminApi.createEmployer(data);
    }

    /**
     * Update employer
     */
    static async updateEmployer(
        employerId: string,
        data: UpdateEmployerRequest
    ): Promise<EmployerWithDetails> {
        console.log('💾 [AdminService] Updating employer:', employerId);
        return await adminApi.updateEmployer(employerId, data);
    }

    /**
     * Delete employer
     */
    static async deleteEmployer(employerId: string): Promise<void> {
        console.log('🗑️ [AdminService] Deleting employer:', employerId);
        return await adminApi.deleteEmployer(employerId);
    }

    // ==================== USER MANAGEMENT ====================

    /**
     * List all users with optional role filter
     */
    static async listUsers(role?: string): Promise<any[]> {
        console.log('👥 [AdminService] Listing users' + (role ? ` (role: ${role})` : ''));
        return await adminApi.listUsers(role);
    }

    /**
     * Get user statistics by role
     */
    static async getUserStats(): Promise<any> {
        console.log('📊 [AdminService] Getting user statistics');
        return await adminApi.getUserStats();
    }

    // ==================== HELPER FUNCTIONS ====================

    /**
     * Get display name for organisation
     */
    static getOrganisationDisplayName(org: Organisation): string {
        return org.name;
    }

    /**
     * Get display name for unit
     */
    static getUnitDisplayName(unit: Unit): string {
        return unit.name;
    }

    /**
     * Check if organisation has active units
     */
    static hasActiveUnits(details: CompleteOrganisationDetails): boolean {
        return details.units.some(unit => unit.is_active);
    }

    /**
     * Check if organisation has active employers
     */
    static hasActiveEmployers(details: CompleteOrganisationDetails): boolean {
        return details.units.some(unit =>
            unit.employers.some(emp => emp.employer.is_active)
        );
    }

    /**
     * Get total employers count
     */
    static getTotalEmployersCount(details: CompleteOrganisationDetails): number {
        return details.stats.total_employers;
    }

    /**
     * Get active employers count
     */
    static getActiveEmployersCount(details: CompleteOrganisationDetails): number {
        return details.stats.active_employers;
    }
}